import { RendererRuntime } from './runtime';
import Layout from './layout/index';
import { recolor } from './utils/color';
import { LayoutBase } from './layout/LayoutBase';

/**
 * @method watch渲染实例
 */
export class WatchfaceRenderer {
  id: string;
  canvas: HTMLCanvasElement;
  ctx: CanvasRenderingContext2D;
  width: number;
  height: number;
  runtime!: RendererRuntime;
  options: WatchRendererOptions;

  tickTimer = 0;
  constructor(canvas: HTMLCanvasElement, options: WatchRendererOptions) {
    this.id = Math.floor(Math.random() * 10000).toString();
    this.options = { ...options };
    this.canvas = canvas;
    const ctx = this.canvas.getContext('2d', { willReadFrequently: true });
    if (!ctx) throw '初始化canvas失败';
    this.ctx = ctx;

    this.width = this.canvas.width ?? 368;
    this.height = this.canvas.height ?? 448;
    this.reset(options);

    // 非常重要，防止出现内存泄漏
    WatchfaceRenderer.clearCache();
  }

  static clearCache() {
    LayoutBase.clearCache();
  }

  // 重置maml实例
  reset(options: WatchRendererOptions): void {
    this.cleanCanvas();
    this.options = {
      ...options
    };
    const {
      ast,
      renderTheme,
      images,
      configData,
      videos = {},
      rive
    } = this.options;
    const { width, height } = this.options;
    if (width) this.width = width;
    if (height) this.height = height;

    this.runtime = new RendererRuntime(
      ast,
      images,
      renderTheme,
      configData,
      videos,
      rive
    );
  }

  recolorIfNeed() {
    // 是否需要重着色
    const { colorMode, themeColor, recolorMode } = this.options.renderTheme;
    if (colorMode != 'recolor' || !themeColor || recolorMode != 'whole') return;

    const { canvas, ctx } = this;
    const x = 0,
      y = 0,
      width = canvas.width,
      height = canvas.height;
    recolor(ctx, { x, y, width, height }, themeColor);
  }

  drawBg(color: string) {
    const { canvas, ctx } = this;
    const { clipRound = 0, scale = 1 } = this.options;
    if (clipRound) {
      ctx.roundRect(
        0,
        0,
        canvas.width / scale,
        canvas.height / scale,
        clipRound
      );
      ctx.fillStyle = color;
      ctx.fill();
      ctx.clip();
    } else {
      ctx.rect(0, 0, canvas.width, canvas.height);
      ctx.fillStyle = color;
      ctx.fill();
    }
  }

  setScale(scale: number) {
    this.options.scale = scale;
  }

  render() {
    // const key = Math.random();

    // 处理缩放问题
    const _scale = this.options.scale ?? 1;

    // 设置宽度和高度，同时可将canvas数据清空
    this.canvas.width = this.width * _scale;
    this.canvas.height = this.height * _scale;
    this.ctx?.scale(_scale, _scale);

    // 清理上次render数据
    this.runtime.state = {};
    this.runtime.configData = {};

    // 首先绘制背景
    if (!this.options.ignoreBg) this.drawBg(this.runtime.bgColor);

    // 绘制各个元素
    const layout = new Layout(this);
    layout.draw(this.runtime.layouts ?? []);

    // 全局recolor，包括文本
    this.recolorIfNeed();
  }

  /**
   * rive的参数需要重置
   *
   * @memberof WatchfaceRenderer
   */
  riveInputsDirty = true;

  /**
   * 持续绘制（通过window.requestAnimationFrame持续绘制）
   */
  renderring() {
    // const key = Math.random();
    if (this.tickTimer) window.cancelAnimationFrame(this.tickTimer);

    // 处理缩放问题
    const _scale = this.options.scale ?? 1;

    // 清理上次render数据
    this.runtime.state = {};
    this.runtime.configData = {};

    // 需要设置rive的input值
    this.riveInputsDirty = true;

    const _draw = () => {
      // 设置宽度和高度，同时可将canvas数据清空
      this.canvas.width = this.width * _scale;
      this.canvas.height = this.height * _scale;
      this.ctx?.scale(_scale, _scale);

      // 首先绘制背景
      if (!this.options.ignoreBg) this.drawBg(this.runtime.bgColor);

      // 绘制各个元素
      const layout = new Layout(this);
      layout.draw(this.runtime.layouts ?? []);

      // 全局recolor，包括文本
      this.recolorIfNeed();

      if (this.options.rive?.canvas) {
        // 如果存在rive则持续绘制
        // 绘制下一帧
        this.tickTimer = window.requestAnimationFrame(_draw);
      }
    };

    _draw();
  }

  /**
   * @method 执行动画
   */
  tick = (options?: {
    /** 一帧播放完毕后 */
    frameRendered?: () => void;
    /** 全部播放完毕后 */
    completed?: () => void;
    // 可选参数，用于指定需要忽略的动画类型，动画类型包括 'ifttt'、'frame' 和 'source-data'
    ignoreAniTypes?: ('ifttt' | 'frame' | 'source-data')[];
  }): void => {
    if (this.tickTimer) window.cancelAnimationFrame(this.tickTimer);

    const { frameRendered, completed, ignoreAniTypes = [] } = options ?? {};

    // 获取每一帧的图层参数
    this.runtime.iftttAnis.forEach((ins) => ins.next());

    // 序列帧动画
    this.runtime.frameAnis.forEach((ins) => ins.next());

    // 数据源数据
    const sourceMap = this.runtime.sourceAni?.next();

    if (sourceMap) this.runtime.initConfigData({}, sourceMap);

    // 渲染这一帧
    this.riveInputsDirty = true;
    this.render();

    // 更新序列帧动画状态
    this.runtime.frameAnis.forEach((ins) => {
      const sprite = this.runtime.getDataItem<ISprite>(
        this.runtime.getLayout(ins.shapeId)?.ref ?? ''
      );
      const total =
        this.runtime.getDataItem<IImageArray>(sprite?.ref ?? '')?.children
          ?.length ?? 0;
      ins.after(total);
    });

    try {
      frameRendered?.();
    } catch (error) {
      console.log(error);
    }

    // 渲染之后，判断动画是否全部执行完成
    const ifttting = ignoreAniTypes.includes('ifttt')
      ? false
      : this.runtime.iftttAnis.some((item) => item.status !== 'completed');
    // 判断数据源是否播放完成
    const sourcePlaying = ignoreAniTypes.includes('source-data')
      ? false
      : this.runtime.sourceAni?.status == 'in-progress';
    // 判断序列帧动画是否完成
    const framePlaying = ignoreAniTypes.includes('frame')
      ? false
      : this.runtime.frameAnis.some((item) => item.status !== 'completed');

    if (ifttting || sourcePlaying || framePlaying) {
      this.tickTimer = requestAnimationFrame(() => this.tick(options));
    } else {
      cancelAnimationFrame(this.tickTimer);
      this.tickTimer = 0;
      try {
        completed?.();
      } catch (error) {
        console.log(error);
      }
    }
  };

  // 清空画布
  cleanCanvas(clear = true): void {
    if (this.tickTimer) {
      cancelAnimationFrame(this.tickTimer);
      this.tickTimer = 0;
    }
    // 清空画布所有元素
    if (clear) this.canvas.width = this.width;
  }

  getShapeState(layoutId: string) {
    return Object.values(this.runtime.state).find(
      (item) => item?._id == layoutId
    );
  }
}
